import { CalendarIcon, ViewGridIcon } from "@heroicons/react/outline";
import Events from "components/Events";
import { PageHeader, PageMain } from "components/layout";
import { type EventListItem } from "hooks/event";
import { Link } from "lib";
import { useTranslation } from "next-i18next";
import { type ReactNode } from "react";

import { useAuth } from "../context/auth-context";

const ShowLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link
    className="inline-block cursor-pointer rounded border border-gray-300 p-2 text-xs leading-4 text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 active:bg-gray-50 active:text-gray-800"
    href={href}
  >
    <div className="flex items-center">
      <CalendarIcon className="mr-1 h-4 w-4 text-gray-400" />
      {children}
    </div>
  </Link>
);

function POSEvent({ event }: { event: EventListItem }) {
  const { t } = useTranslation();

  return (
    <Events.Item event={event}>
      {event.shows.map((show) => (
        <ShowLink key={show.id} href={`/events/${event.id}/sale?showId=${show.id}`}>
          {show.getDescription()}
        </ShowLink>
      ))}
      {event.hasCombos() ? (
        <ShowLink key="combos" href={`/events/${event.id}/sale?showId=combo`}>
          {t("generic.combo")}
        </ShowLink>
      ) : null}
    </Events.Item>
  );
}

function BackOfficeEvent({ event }: { event: EventListItem }) {
  const { t } = useTranslation();

  return (
    <Events.Item event={event}>
      <Link
        className="inline-block cursor-pointer rounded border border-primary-700 p-2 text-xs leading-4 text-primary-700 hover:bg-primary-700 hover:text-white active:bg-primary-700 active:text-white"
        href={`/events/${event.id}/saleFromHold`}
      >
        <div className="flex items-center">
          <ViewGridIcon className="mr-1 h-4 w-4" />
          {t("holds.emitFromHold")}
        </div>
      </Link>
      <Link
        className="inline-block cursor-pointer rounded border border-gray-300 p-2 text-xs leading-4 text-gray-700 hover:border-gray-700 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 active:bg-gray-50 active:text-gray-800"
        href={`/events/${event.id}/sale`}
      >
        <div className="flex items-center">
          <CalendarIcon className="mr-1 h-4 w-4" />
          {t("holds.emitFromStock")}
        </div>
      </Link>
    </Events.Item>
  );
}

function EventsMainPage() {
  const { t } = useTranslation();
  const { isBackoffice } = useAuth();

  return (
    <>
      <PageHeader subtitle={t("generic.sale")} title={t("generic.events")} />
      <PageMain>
        <Events.List component={isBackoffice ? BackOfficeEvent : POSEvent} />
      </PageMain>
    </>
  );
}

export { getServerSideProps } from "utils/i18n";

export default EventsMainPage;
